/**
 * The top of the Day timeline (user, 19/10/2026: "it's really ugly — I want
 * to see more info"): the day in numbers — drops, SMS, lists and gateways in
 * use, the first and last drop (Danish first).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { usd } from '@/lib/reporting-currency';

import type { DayPlanPreview } from '../../../drop-generator.types';
import { TogglePill } from '../section/SectionToggle';
import { instantText } from '../../../utils/clock/dk-label';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { timelineFacts } from './timeline-facts';

const TILE = 'flex min-w-[118px] flex-1 flex-col gap-0.5 rounded-lg border border-border/50 bg-background px-3 py-2 text-left';

const TileBody: FC<{ label: string; value: ReactNode; sub?: ReactNode }> = ({ label, value, sub }) => (
  <>
    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="font-mono text-base font-semibold tabular-nums">{value}</span>
    {sub ? <span className="truncate text-[11px] text-muted-foreground">{sub}</span> : null}
  </>
);

const Tile: FC<{ label: string; value: ReactNode; sub?: ReactNode }> = (props) => (
  <div className={TILE}>
    <TileBody {...props} />
  </div>
);

/** The "Lists in use" tile opens the list (19/10/2026). */
const ListsTile: FC<{ count: number; open: boolean; onClick: () => void }> = ({ count, open, onClick }) => (
  <button
    type="button"
    aria-expanded={open}
    onClick={onClick}
    className={`${TILE} group transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${open ? 'border-primary/50 bg-primary/5' : ''}`}
  >
    <TileBody
      label="Lists in use"
      value={count}
      sub={<TogglePill open={open} className="mt-0.5" />}
    />
  </button>
);

export const TimelineSummary: FC<{ preview: DayPlanPreview; listsOpen: boolean; onLists: () => void }> = ({ preview, listsOpen, onLists }) => {
  const clock = useCountryClock();
  const f = timelineFacts(preview);
  const at = (iso: string | null) => (iso ? instantText(iso, clock) : '—');
  const [firstDk, firstLocal] = at(f.firstAt).split(' · ');
  const [lastDk, lastLocal] = at(f.lastAt).split(' · ');
  return (
    <div className="flex flex-col gap-3 border-b border-border/40 px-5 py-4" data-testid="timeline-summary">
      <div className="flex flex-wrap gap-2">
        <Tile label="Drops" value={f.drops} sub={`${f.containers} container${f.containers === 1 ? '' : 's'} send`} />
        <Tile label="SMS" value={f.sms.toLocaleString('en-US')} sub={f.cost !== null ? `≈ ${usd(f.cost, 0)}` : 'cost unknown'} />
        <ListsTile count={f.lists} open={listsOpen} onClick={onLists} />
        <Tile label="Gateways" value={f.gateways} sub="in use today" />
        <Tile label="First drop" value={firstDk} sub={firstLocal} />
        <Tile label="Last drop" value={lastDk} sub={lastLocal} />
      </div>
    </div>
  );
};
